import { html, LitElement, TemplateResult } from 'lit';
import { property, queryAssignedNodes } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { parts } from '../util/exportPart';

export interface BlocksElementProps {
  styleModifier?: string;
}

export interface DetailObj {
  [key: string]: unknown;
}

export interface BlocksDispatchProps {
  e?: Event;
  eventName: string;
  detailObj?: DetailObj;
  optionsObj?: { [key: string]: unknown };
}

export interface BlocksEvent extends Event {
  detail: {
    originalEvent: Event;
    detailObj: DetailObj;
  };
}

/**
 * A base element.
 */
export class BlocksElement extends LitElement {
  /**
   * Append to the class name. Used for passing in utility classes
   */
  @property({ type: String })
  styleModifier = '';

  /**
   * Abstraction of `classMap` that automatically includes any style modifier
   * as well as any set variants.
   *
   * It is expected that `variant` would be overridden in a subclass with more
   * specific types, `@property({ type: String }) variant?: 'foo' | 'bar'`
   *
   * @param baseClassName
   */
  componentClassNames(baseClassName: string, additionalClassNames = {}) {
    //https://github.com/lit/lit/issues/3095
    const createClassObj = this.styleModifier?.split?.(' ').reduce((acc, curr) => {
      if (curr !== '') {
        (acc as Record<string, boolean>)[curr] = true;
      }
      return acc;
    }, {});

    return classMap({
      [baseClassName]: !!baseClassName,
      ...createClassObj,
      ...additionalClassNames
    });
  }

  /**
   * Check if a slot is empty
   *
   * @param slotName
   */
  slotEmpty(slotName?: string) {
    return !this.querySelector(`[slot${slotName ? `="${slotName}"` : ''}]`);
  }

  /**
   * Check if a slot is not empty
   *
   * @param slotName
   */
  slotNotEmpty(slotName?: string) {
    if (!this.slotEmpty(slotName) !== false) {
      return !this.slotEmpty(slotName);
    } else {
      return;
    }
  }

  /**
   * TODO: This fix to be tested for all components
   * Check if a slot is not empty
   * @param slotName
   */
  public hasEmptySlot(slotName: string): boolean {
    return Array.from(this.children ?? []).some((el) => el?.getAttribute('slot') === slotName);
  }

  /**
   * Dispatch a custom event.
   */
  dispatch({ e, eventName, detailObj = {}, optionsObj = {} }: BlocksDispatchProps): CustomEvent {
    const options = {
      bubbles: true,
      composed: true,
      ...optionsObj,
      detail: { ...(e && { originalEvent: e }), ...detailObj }
    };
    const event = new CustomEvent(eventName, options);
    this.dispatchEvent(event);
    return event;
  }

  /**
   * To query the slotted element in custom-link slot
   */

  @queryAssignedNodes({ slot: 'custom-link', flatten: true }) protected _children?: Array<BlocksElement>;

  /**
   * This generates exportpart attributes for the nested block components with the reference of part names from  `exportPart.ts` file
   * @param componentName - The component's name which will be prefixed along with the part attribute
   * @param commonComponent - If this optional props is defined, will fetch value from parts object, based on this property instead of `componentName`. Useful when icon-name has to be prefixed but it can be passed as `icon`.
   * @returns expoprtparts which is a string in csv format
   */
  getExportParts(componentName: string, commonComponent?: string) {
    return parts[commonComponent ? commonComponent : componentName]?.reduce(
      (acc: string, key: string) => `${acc}${acc ? ',' : ''}${key}:${componentName}__${key}`,
      ''
    );
  }
  /**
   * Example render, should not be used
   */
  render(): TemplateResult<any> {
    return html` <slot></slot> `;
  }
}
